import {Component, EventEmitter, OnInit, ViewChild} from '@angular/core';
import {AnessaTranslationService} from '../../../helper/translation/anessa-translation.service';
import {ProjectCommunicationService} from '../../../communication/project-comms.service';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import {take, takeUntil, takeWhile} from 'rxjs/operators';
import {Project} from '../../../models/project/project.model';
import {InputSummary, InputSummaryItem} from '../../../models/input-summary';
import Utils from '../../../util';
import {ResultsTreetableComponent} from '../../results-helper/results-treetable/results-treetable.component';
import { isEmpty } from 'lodash-es';
import {ResultsSummarySaveDialogComponent} from '../baseline-summary/results-summary-save-dialog/results-summary-save-dialog.component';
import {ConfiguredToastrService} from '../../../helper/toastr/configured-toastr.service';
import {MatDialog} from '@angular/material/dialog';
import {AggregationMethod} from '../../results-helper/grouped-result-table/grouped-result-table.component';
import {TreeTableNode} from '../../results-helper/results-treetable/ng-material-treetable/models';
import {
  ResultSetConfig,
  ResultSetConfigInterface,
  ResultSetID,
  ResultSetIds,
} from '../../../models/results/result-set-config.model';
import {
  ResultSetDropdownId
} from '../../../models/enums/results/result-set-dropdown-ids/result-set-dropdown-config-id.enum';
import { ResultSetConfigService } from '../../results-helper/result-set-config.service';
import {
  DisplayOption, DisplayOptionStatus
} from '../../../models/results/baseline/baseline-summary-display-option/baseline-summary-display-option.model';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { ReportConfig } from '../../../models/report/report.model';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { LooseObject } from '../../../models/loose-object.model';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Results } from '../../../models/results/results.model';
import { ReportElement } from '../baseline-summary/baseline-summary.component';
import ResultsUtils from '../../results-util';
import { CommunicationService } from '../../../communication/communication.service';

@Component({
  selector: 'app-baseline-summary-detailed',
  templateUrl: './baseline-summary-detailed.component.html',
  styleUrls: ['./baseline-summary-detailed.component.scss']
})
export class BaselineSummaryDetailedComponent extends UserpilotReloader implements OnInit {

  categoriesOptions = ['RESULTS.SUMMARY.ALL', 'RESULTS.SUMMARY.INPUTS', 'RESULTS.SUMMARY.OUTPUTS'];
  categorySet = 'RESULTS.SUMMARY.ALL';
  displayOptions: DisplayOption[] = [];
  displayOptionsTemp: DisplayOption[] = [];
  optionsRoot = new DisplayOption('root_label', 'root_name', undefined, 0, null);
  translatedResultSelectGroups: any = {};
  translatedResultSelectOptions: any = {};
  currencyOptions: Unit[] = [];
  currencySet: Unit;
  measurementOptions = ['RESULTS.GENERAL.METRIC', 'RESULTS.GENERAL.IMPERIAL'];
  // measurementLabels = {'RESULTS.GENERAL.METRIC': 'metric', 'RESULTS.GENERAL.IMPERIAL': 'imperial'};
  measurementSet = 'RESULTS.GENERAL.METRIC';
  periodOptions = ['RESULTS.GENERAL.DAY', 'RESULTS.GENERAL.MONTH', 'RESULTS.GENERAL.YEAR'];
  periodSet = 'RESULTS.GENERAL.YEAR';

  projectState: Project | null = null;
  inputsSummary: InputSummary | null = null;
  inputsData: InputSummary | null = null;
  results: Results | null = null;
  exchangeRateDate: string;
  outputsData = {};

  aggregationMethods = AggregationMethod;

  /** trigger for translation pipe */
  trigger?: string;

  onDestroyNotifier = new EventEmitter();

  @ViewChild('treetableInputs') treetableInputs?: ResultsTreetableComponent;
  @ViewChild('treetableValueUnitResults') treetableValueUnitResults?: ResultsTreetableComponent;
  @ViewChild('treetableYearlyResults') treetableYearlyResults?: ResultsTreetableComponent;

  // RESULT SET DEFAULT CONFIGURATION
  optsConf?: ResultSetConfig;
  periodConf?: ResultSetConfig;
  ResultSetIDEnum = ResultSetID;

  PROJECT_ID?: string | null;
  projectLoaded = false;
  unitServiceReady = false;
  resultsLoaded = false;
  currencyOptionsReady = false;

  constructor(
    private translationService: AnessaTranslationService,
    private projectCommService: ProjectCommunicationService,
    private unitService: UnitService,
    private toastrService: ConfiguredToastrService,
    private dialog: MatDialog,
    private resultSetConfigService: ResultSetConfigService,
    private commService: CommunicationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.resultsLoaded = loaded;
        if (loaded) {
          const tempResults = Utils.deepCopy(this.projectCommService.getResults());
          if (!tempResults) return;
          this.addAdditionalRNGRows(tempResults);
          this.results = tempResults;
          this.projectState = new Project(this.projectCommService.getResultProjectState());
          this.displayOptions = [];
          this.setCurrenciesOptions(this.results.created); // calls setup
        }
      });
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((ready: boolean) => {
        this.unitServiceReady = ready;
        if (ready) {
          this.setup();
        }
      });
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((loaded: boolean) => {
        this.projectLoaded = loaded;
        if (loaded) {
          this.PROJECT_ID = this.projectCommService.getProjectId();
          this.setup();
        }
      });
      if (this.projectCommService.getProjectInfo().defaults?.unitSystem) {
        const unitSystem = this.projectCommService.getProjectInfo().defaults.unitSystem;
        this.measurementSet = unitSystem === 'imperial' ? 'RESULTS.GENERAL.IMPERIAL' : 'RESULTS.GENERAL.METRIC';
    } else if (this.projectCommService.getProjectDefaults().gas === '000000000000000000003302') {
      this.measurementSet = 'RESULTS.GENERAL.IMPERIAL';
    }
  }

  setup() {
    if (this.projectLoaded && this.unitServiceReady && this.resultsLoaded && this.currencyOptionsReady) {
      this.setDisplayOptions();
      // calls setTableData regardless of if defaults are set
      this.fetchResultSetConfigData();
    }
  }

  fetchResultSetConfigData() {
    if (!this.PROJECT_ID) return;
    if (this.projectLoaded && this.unitServiceReady && this.resultsLoaded) {
      this.resultSetConfigService.fetch(
        [ResultSetID.DETAILED_SUMMARY_OPTS, ResultSetID.DETAILED_SUMMARY_PERIOD],
        this.PROJECT_ID
      )
        .pipe(take(1))
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(res => {
          this.setupResultSetConfigs(res);
          this.updateAllResults();
          this.setTableData();
        });
    }
  }

  private addAdditionalRNGRows(tempResults: Results) {
    if (!tempResults.technical ||
      !tempResults.technical.ppd ||
      !tempResults.technical.ppd.sp ||
      !tempResults.technical.ppd.sp['RESULTS.DATA.TECHNICAL.RNGP']) {
      return;
    }
    const spNode = tempResults.technical.ppd.sp;
    const rngNode = spNode['RESULTS.DATA.TECHNICAL.RNGP'];
    const tempNodes: LooseObject = {};
    tempNodes['RESULTS.DATA.TECHNICAL.RNGP'] = Utils.deepCopy(spNode['RESULTS.DATA.TECHNICAL.RNGP']);

    // add GJ to the result set
    const gjRes = Utils.deepCopy(rngNode);
    const gjUnit = this.unitService.getUnitById('000000000000000000003814');
    gjRes.values[0] = gjUnit.convertFromBase(gjRes.values[0]);
    gjRes.unit = gjUnit.id;
    gjRes.label = 'RESULTS.DATA.RNG_PRODUCTION_GJ';
    tempNodes['RESULTS.DATA.RNG_PRODUCTION_GJ'] = gjRes;

    // add GWH to the result set
    const gwhRes = Utils.deepCopy(rngNode);
    const gwhUnit = this.unitService.getUnitById('000000000000000000003815');
    gwhRes.values[0] = gwhUnit.convertFromBase(gwhRes.values[0]);
    gwhRes.unit = gwhUnit.id;
    gwhRes.label = 'RESULTS.DATA.RNG_PRODUCTION_GWH';
    tempNodes['RESULTS.DATA.RNG_PRODUCTION_GWH'] = gwhRes;


    // add GGE to the result set
    const ggeRes = Utils.deepCopy(rngNode);
    const ggeUnit = this.unitService.getUnitById('000000000000000000003811');
    ggeRes.values[0] = ggeUnit.convertFromBase(ggeRes.values[0]);
    ggeRes.unit = ggeUnit.id;
    ggeRes.label = 'RESULTS.DATA.RNG_PRODUCTION_GGE';
    tempNodes['RESULTS.DATA.RNG_PRODUCTION_GGE'] = ggeRes;


    // add MMBtu to the result set
    const mmbtuRes = Utils.deepCopy(rngNode);
    const mmbtuUnit = this.unitService.getUnitById('000000000000000000003810');
    mmbtuRes.values[0] = mmbtuUnit.convertFromBase(mmbtuRes.values[0]);
    mmbtuRes.unit = mmbtuUnit.id;
    mmbtuRes.label = 'RESULTS.DATA.RNG_PRODUCTION_MMBTU';
    tempNodes['RESULTS.DATA.RNG_PRODUCTION_MMBTU'] = mmbtuRes;

    // add back rest of sold products
    for (const n of Object.keys(spNode)) {
      if (!tempNodes[n]) tempNodes[n] = Utils.deepCopy(spNode[n]);
    }
    tempResults.technical.ppd.sp = Utils.deepCopy(tempNodes);
  }

  setCurrenciesOptions(created: string) {
    this.unitService.getCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyOptions = units;
        // sort rates alphabetically
        this.currencyOptions.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        // Select default unit if set or CAD otherwise
        if (this.projectCommService.getProjectDefaults().currency != null) {
          this.currencySet = this.unitService.getUnitById(this.projectCommService.getProjectDefaults().currency);
        } else {
          this.currencySet = this.currencyOptions.find(currency => currency.displayName === 'CAD');
        }
        this.currencyOptionsReady = true;
        this.setup();
      }
    );
  }

  filterDisplayOptions(optionsFilter: string): void {
    if (!optionsFilter) {
      this.displayOptions.forEach(option => {
        option.hidden = false;
        option.filterMatched = false;
      });
      return;
    }
    const filter = optionsFilter.toLowerCase();
    let filterDepth = 0;
    for (const option of this.displayOptions) {
      const matched = option.name.toLowerCase().includes(filter);
      if (filterDepth) {
        if (option.depth > filterDepth) {
          option.hidden = false;
          option.filterMatched = matched;
          continue;
        } else {
          filterDepth = 0;
        }
      }
      option.hidden = !matched;
      if (matched) {
        option.filterMatched = true;
        filterDepth = option.depth;
        let parent = option.parent;
        while (parent) {
          parent.hidden = false;
          parent = parent.parent;
        }
      } else {
        option.filterMatched = false;
      }
    }
  }

  setTableData() {
    if (this.projectState && this.results) {
      if (this.currencySet && this.displayOptions.length > 0) {
        this.inputsData = this.inputsSummary;
        this.outputsData = this.results;
      }
    }
  }

  setDisplayOptions() {
    if (!this.projectState || !this.results) {
      throw Error('attempting to set display options before project and results' +
        'are ready');
    }
    if (this.displayOptions.length > 0) { return; }
    this.displayOptionsTemp = [];
    this.optionsRoot.result = this.results;
    // add inputs options
    this.inputsSummary = new InputSummary();
    this.inputsSummary.subSection = this.projectState.getInputSummary(true);
    this.inputSummaryToOptions('RESULTS.SUMMARY.PROJECT_INPUTS', this.inputsSummary, this.optionsRoot);
    // add outputs options
    const resultsOptionName = this.translationService.get('RESULTS.SUMMARY.SIMULATION_RESULTS');
    const resultsRoot = this.optionsRoot.makeChild('RESULTS.SUMMARY.SIMULATION_RESULTS', resultsOptionName, this.results, false, 'result');
    this.displayOptionsTemp.push(resultsRoot);
    this.resultsToOptions(this.results, resultsRoot);
    // set display options
    this.displayOptions = this.displayOptionsTemp;
  }

  inputSummaryToOptions(inputKey: string, inputSummary: InputSummary, parent: DisplayOption) {
    if (inputSummary.inputs.length === 0) {
      if (isEmpty(inputSummary.subSection)) { return; } // empty input summary
      const subSectionKeys = Object.keys(inputSummary.subSection);
      if (subSectionKeys.length === 1 && subSectionKeys[0] === '') {
        // some results are weird like this (project info, digester)
        const weirdKey = '' as keyof typeof inputSummary.subSection;
        inputSummary = inputSummary.subSection[weirdKey];
      }
    }
    const name = this.translationService.get(inputKey, {}, false, false);
    const newOption = parent.makeChild(inputKey, name, inputSummary, false, 'input');
    this.displayOptionsTemp.push(newOption);
    for (const input of inputSummary.inputs) {
      this.addInputLeafOption(input, newOption, 'input');
    }
    if (inputSummary.subSection && !isEmpty(inputSummary.subSection)) {
      const subSection = inputSummary.subSection;
      for (const key of Object.keys(subSection)) {
        if (key.startsWith('aux.')) { continue; }
        const subKey = key as keyof typeof subSection;
        this.inputSummaryToOptions(key, subSection[subKey], newOption);
      }
    }
  }

  addInputLeafOption(input: InputSummaryItem, parent: DisplayOption, source?: string): DisplayOption {
    const label = input.name;
    const name = this.translationService.get(label, {}, false, false);
    const newOption = parent.makeChild(label, name, input, true, source);
    newOption.type = 'value_unit';
    this.displayOptionsTemp.push(newOption);
    return newOption;
  }

  resultsToOptions(result: LooseObject, parent: DisplayOption) {
    if (this.resultIsLeaf(result)) {
      const myRow = result as ResultRow;
      this.addResultLeafOption(myRow, parent, 'result');
    } else {
      let parentType;
      for (const key of Object.keys(result)) {
        if (key.startsWith('aux.')) { continue; }
        let newOption: DisplayOption | null = null;
        const subResult = result[key];
        if (!subResult) { continue; }
        if (this.resultIsLeaf(subResult)) {
          newOption = this.addResultLeafOption(subResult, parent, 'result');
        } else if (subResult && typeof subResult === 'object' && !Array.isArray(subResult)) {
          const name = this.translationService.get(key, {}, false, false);
          newOption = parent.makeChild(key, name, subResult, false, 'result');
          this.displayOptionsTemp.push(newOption);
          this.resultsToOptions(subResult, newOption);
        }
        // infer parent type
        if (parentType !== 'mixed' && newOption) {
          if (!parentType) {
            parentType = newOption.type;
          } else {
            if (parentType !== newOption.type) {
              parentType = 'mixed';
            }
          }
        }
      }
      parent.type = parentType;
    }
  }

  addResultLeafOption(result: ResultRow, parent: DisplayOption, source?: string): DisplayOption {
    const label = result.label;
    const name = this.translationService.get(label);
    const newOption = parent.makeChild(label, name, result, true, source);
    if (result.values.length > 1) {
      newOption.type = 'yearly';
    } else if (result.unit && result.values.length === 1) {
      newOption.type = 'value_unit';
    }
    this.displayOptionsTemp.push(newOption);
    return newOption;
  }

  resultIsLeaf(result: LooseObject) {
    return ['RESULTS.DATA.LABEL', 'RESULTS.DATA.VALUES']
      .every(leafKey => Object.keys(result).includes(leafKey));
  }

  updateRNGProducts() {
    const rng = this.displayOptions.find(opt => opt.label === 'RESULTS.DATA.TECHNICAL.RNGP');
    const gj = this.displayOptions.find(opt => opt.label === 'RESULTS.DATA.RNG_PRODUCTION_GJ');
    const gwh = this.displayOptions.find(opt => opt.label === 'RESULTS.DATA.RNG_PRODUCTION_GWH');
    const gge = this.displayOptions.find(opt => opt.label === 'RESULTS.DATA.RNG_PRODUCTION_GGE');
    const mmbtu = this.displayOptions.find(opt => opt.label === 'RESULTS.DATA.RNG_PRODUCTION_MMBTU');
    if (gge) gge.status = 'unchecked';
    if (mmbtu) mmbtu.status = 'unchecked';
    if (gj) gj.status = 'unchecked';
    if (gwh) gwh.status = 'unchecked';
    if (rng?.status === 'checked') {
      if (this.measurementSet === 'RESULTS.GENERAL.METRIC') {
        if (gj) {
          gj.status = 'checked';
        }
        if (gwh) {
          gwh.status = 'checked';
        }
      }
      if (this.measurementSet === 'RESULTS.GENERAL.IMPERIAL') {
        if (gge) {
          gge.status = 'checked';
        }
        if (mmbtu) {
          mmbtu.status = 'checked';
        }
      }
    }
  }

  optionChanged(option: DisplayOption, $event: MatCheckboxChange) {
    const status = $event.checked ? 'checked' : 'unchecked';
    option.setStatus(status);
    this.updateRNGProducts();
    if (option.source === 'input') {
      if (this.treetableInputs) { this.treetableInputs.reprocessInputsData(); }
    } else if (option.source === 'result') {
      if (this.treetableValueUnitResults) { this.treetableValueUnitResults.reprocessResultsData(); }
      if (this.treetableYearlyResults) { this.treetableYearlyResults.reprocessResultsData(); }
    } else {
      this.updateAllResults();
    }
  }

  updateAllResults() {
    if (this.treetableInputs) this.treetableInputs.reprocessInputsData();
    if (this.treetableValueUnitResults) this.treetableValueUnitResults.reprocessResultsData();
    if (this.treetableYearlyResults) this.treetableYearlyResults.reprocessResultsData();
  }

  showDisplayOption(option: DisplayOption) {
    if (option.label === 'RESULTS.DATA.RNG_PRODUCTION_GJ') return false;
    if (option.label === 'RESULTS.DATA.RNG_PRODUCTION_GWH') return false;
    if (option.label === 'RESULTS.DATA.RNG_PRODUCTION_GGE') return false;
    if (option.label === 'RESULTS.DATA.RNG_PRODUCTION_MMBTU') return false;
    if (option.hidden) { return false; }
    if (this.categorySet === 'RESULTS.SUMMARY.INPUTS' && option.source === 'result') { return false; }
    if (this.categorySet === 'RESULTS.SUMMARY.OUTPUTS' && option.source === 'input') { return false; }
    return true;
  }

  saveToPdf() {
    this.toastrService.success(this.translationService.get('SERVICE.REPORT.REPORT_GENERATING'), undefined);
    const saveDialog = this.dialog.open(ResultsSummarySaveDialogComponent, {
      width: 'max-content',
      data: {category: this.categorySet, page: 'detailed'}
    });
    saveDialog.afterClosed().pipe(take(1)).subscribe(reportConfig => {
      if (reportConfig) { reportConfig.format = 'pdf'; }
      this.saveToReport(reportConfig);
    });
  }

  saveToReport(reportConfig: ReportConfig) {
    if (!this.treetableInputs || ! this.treetableInputs.treeTable
      || !this.treetableValueUnitResults || !this.treetableValueUnitResults.treeTable
      || !this.treetableYearlyResults || !this.treetableYearlyResults.treeTable
    ) return;

    if (reportConfig) {
      let elements: ReportElement[] = [];
      // opening page
      const openingPage = new ReportElement('opening page', 'opening page',
        {title: reportConfig.title, author: reportConfig.author},
        null, 'after');
      elements.push(openingPage);
      // inputs table
      if (reportConfig.inputs && !this.tableIsEmpty(this.treetableInputs)) {
        const inputsTable = new ReportElement('treetable', 'inputs summary table',
          this.getExpandedCopy(this.treetableInputs.treeTable['treeTable']),
          this.treetableInputs.options.customColumnOrder);
        elements.push(inputsTable);
      }
      // outputs table
      if (reportConfig.outputs && !this.tableIsEmpty(this.treetableValueUnitResults)) {
        const outputsTable = new ReportElement('treetable', 'outputs summary table',
          this.getExpandedCopy(this.treetableValueUnitResults.treeTable['treeTable']),
          this.treetableValueUnitResults.options.customColumnOrder);
        elements.push(outputsTable);
      }
      // yearly outputs table
      if (reportConfig.outputsYearly && !this.tableIsEmpty(this.treetableYearlyResults)) {
        const yearlyOutputsTable = new ReportElement('treetable', 'yearly outputs summary table',
          this.getExpandedCopy(this.treetableYearlyResults.treeTable['treeTable']),
          this.treetableYearlyResults.options.customColumnOrder);
        elements.push(yearlyOutputsTable);
      }
      // add sub tag to avoid the # at CH in the report
      if (elements[1] !== undefined && elements[1].content) {
        Object.values(elements[1].content).forEach(row => {
          if ((row['value']['RESULTS.DATA.LABEL']).startsWith('CH₄')) {
            row['value']['RESULTS.DATA.LABEL'] = 'CH<sub>4</sub> concentration';
          }
        });
      }

      elements = Utils.deepCopy(elements);

      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        if (element.columns) {
          for (let y = 0; y < element.columns?.length; y++) {
            element.columns[y] = this.translationService.get(element.columns[y]);
          }
        }
        for (const key of Object.keys(element.content)) {
          this.formatNodeRecursively(element.content[key]);
        }
      }

      // generate report
      const reportPack = {elements: elements, config: reportConfig};
      ResultsUtils.generateResultsSummaryReportCall(reportPack, this.commService, this.translationService, this.toastrService);
    }
  }



  formatNodeRecursively(element: LooseObject) {
    if (!element) return;
    if (element['value']) {
      for (const key of Object.keys(element.value)) {
        const t = this.translationService.get(key);
        element.value[t] = element.value[key];
        if (key === 'RESULTS.DATA.LABEL' || key === 'RESULTS.DATA.VALUE' || key === 'RESULTS.DATA.UNIT') delete element.value[key];
      }
    }
    if (element.children || element.content?.children) {
      for (const childNode of element.children) {
        this.formatNodeRecursively(childNode);
      }
    }
  }

  getExpandedCopy(originalTreeTable: TreeTableNode<any>[]) {
    const treeTable = Utils.deepCopy(originalTreeTable);
    for (const row of treeTable) {
      row.isExpanded = true;
      row.isVisible = true;
    }
    return treeTable;
  }

  tableIsEmpty(treetable: ResultsTreetableComponent) {
    return !treetable || !treetable.treeTable || treetable.treeTable['treeTable'].length === 0;
  }

  saveToExcel() {
    const reportConfig: ReportConfig = {
      title: 'detailed results summary',
      format: 'excel',
      inputs: true,
      outputs: true,
      outputsYearly: true
    };
    this.saveToReport(reportConfig);
  }

  unsetDefaults(resultSetID: ResultSetID) {
    if (!this.PROJECT_ID) return;

    switch (resultSetID) {
      case ResultSetID.DETAILED_SUMMARY_OPTS:
        if (this.optsConf?.dropdownConfigEnabled) {
          this.optsConf.dropdownConfigEnabled = false;
          this.resultSetConfigService.save(this.optsConf, this.PROJECT_ID);
        }
        break;
      case ResultSetID.DETAILED_SUMMARY_PERIOD:
        if (this.periodConf?.dropdownConfigEnabled) {
          this.periodConf.dropdownConfigEnabled = false;
          this.resultSetConfigService.save(this.periodConf, this.PROJECT_ID);
        }
    }
  }

  setupResultSetConfigs(res: ResultSetIds): void {
    if (!res && !res['obj']) {
      throw Error('invalid response from result set config, unable to process');
    }
    const processedObj = Utils.restoreMapsInJson(res['obj'], 'dropdownDefaults');
    const optsConfObj: ResultSetConfigInterface = processedObj[ResultSetID.DETAILED_SUMMARY_OPTS];
    if (optsConfObj) {
      this.optsConf = new ResultSetConfig(optsConfObj);
    } else {
      this.optsConf = new ResultSetConfig();
      this.optsConf.resultSetID = ResultSetID.DETAILED_SUMMARY_OPTS;
    }
    const periodConfObj: ResultSetConfigInterface = processedObj[ResultSetID.DETAILED_SUMMARY_PERIOD];
    if (periodConfObj) {
      this.periodConf = new ResultSetConfig(periodConfObj);
    } else {
      this.periodConf = new ResultSetConfig();
      this.periodConf.resultSetID = ResultSetID.DETAILED_SUMMARY_PERIOD;
    }
    this.processResultSetDefaults();
  }

  processResultSetDefaults() {
    if (!this.unitServiceReady || !this.projectLoaded) return;
    this.processOptsConf();
    this.processPeriodConf();
  }

  processOptsConf() {
    if (this.optsConf?.dropdownConfigEnabled && Utils.isMap(this.optsConf.dropdownDefaults)) {
      const defaults = this.optsConf.dropdownDefaults;
      if (defaults.has(ResultSetDropdownId.DETAILED_SUMMARY_CAT)) {
        this.categorySet = defaults.get(ResultSetDropdownId.DETAILED_SUMMARY_CAT);
      }
      if (defaults.has(ResultSetDropdownId.DETAILED_SUMMARY_SELECTED_RES)) {
        this.processSelectedResDefaults(defaults.get(ResultSetDropdownId.DETAILED_SUMMARY_SELECTED_RES));
      }
      if (defaults.has(ResultSetDropdownId.DETAILED_SUMMARY_CURR)) {
        const defaultCur = defaults.get(ResultSetDropdownId.DETAILED_SUMMARY_CURR);
        this.currencySet = this.currencyOptions.find(
          opt => opt.id === defaultCur
        );
      }
      if (defaults.has(ResultSetDropdownId.DETAILED_SUMMARY_UNIT_TYPE)) {
        this.measurementSet = defaults.get(ResultSetDropdownId.DETAILED_SUMMARY_UNIT_TYPE);
      }
    }
  }

  processSelectedResDefaults(defaults: any) {
    for (const item of defaults) {
      if (this.displayOptions?.length > 0) {
        const displayOpt = this.displayOptions.find(opt => opt.getName() === item['name']);
        if (displayOpt) {
          displayOpt.setStatus(item['status']);
        }
      }
    }
    this.filterDisplayOptions('');
  }

  processPeriodConf() {
    if (this.periodConf?.dropdownConfigEnabled && this.periodConf.dropdownDefaults) {
      const defaults = this.periodConf.dropdownDefaults;
      if (defaults.has(ResultSetDropdownId.DETAILED_SUMMARY_PERIOD)) {
        this.periodSet = defaults.get(ResultSetDropdownId.DETAILED_SUMMARY_PERIOD);
      }
    }
  }

  onResultSetDefaultChange(resultSetID: ResultSetID): void {
    switch (resultSetID) {
      case ResultSetID.DETAILED_SUMMARY_OPTS:
        this.updateAndSaveOptsConfig();
        break;
      case ResultSetID.DETAILED_SUMMARY_PERIOD:
        this.updateAndSavePeriodConfig();
    }
  }

  updateAndSaveOptsConfig(): void {
    if (!this.PROJECT_ID || !this.optsConf) return;

    if (this.optsConf.dropdownConfigEnabled && Utils.isMap(this.optsConf.dropdownDefaults)) {
      this.optsConf.dropdownDefaults.set(ResultSetDropdownId.DETAILED_SUMMARY_CAT, this.categorySet);
      this.optsConf.dropdownDefaults.set(ResultSetDropdownId.DETAILED_SUMMARY_SELECTED_RES, this.getSelectedResultsObj());
      this.optsConf.dropdownDefaults.set(ResultSetDropdownId.DETAILED_SUMMARY_CURR, this.currencySet?.id);
      this.optsConf.dropdownDefaults.set(ResultSetDropdownId.DETAILED_SUMMARY_UNIT_TYPE, this.measurementSet);
    }
    this.resultSetConfigService.save(this.optsConf, this.PROJECT_ID);
  }

  private getSelectedResultsObj(): {name: string, status: string}[] {
    const defaultsData: {name: string, status: string}[] = [];
    if (this.displayOptions?.length > 0) {
      for (const item of this.displayOptions) {
        if (item.status === DisplayOptionStatus.CHECKED) {
          defaultsData.push({name: item.getName(), status: item.status});
        }
      }
    }
    return defaultsData;
  }

  updateAndSavePeriodConfig(): void {
    if (!this.PROJECT_ID || !this.periodConf) return;

    if (this.periodConf?.dropdownConfigEnabled && Utils.isMap(this.periodConf.dropdownDefaults)) {
      this.periodConf.dropdownDefaults.set(ResultSetDropdownId.DETAILED_SUMMARY_PERIOD, this.periodSet);
    }
    this.resultSetConfigService.save(this.periodConf, this.PROJECT_ID);
  }

}
